import React, { KeyboardEvent } from 'react';
import styled from 'styled-components';
import { useObserver } from 'mobx-react-lite';

import useUI from 'src/hooks/use-ui';
import useInstoreCart from 'hooks/use-instore-cart';
import useTranslation from 'hooks/use-translation';
import { TotalItem } from './total-item';

export type TotalsProps = {
  paymentComplete?: boolean;
};

export const Totals = ({ paymentComplete }: TotalsProps): JSX.Element => {
  const UI = useUI();
  const InstoreCart = useInstoreCart();
  const totalDiscounts = useObserver(() => InstoreCart.totalDiscounts);
  const subtotalCents = useObserver(() => InstoreCart.subtotalCents);
  const totalFees = useObserver(() => InstoreCart.totalFees);
  const totalTaxes = useObserver(() => InstoreCart.totalTaxes);
  const tip = useObserver(() => InstoreCart.tip);
  const totalPrice = useObserver(() => InstoreCart.totalPrice);
  const { t } = useTranslation();

  const labels = {
    subtotal: t('cart.subtotal', 'Subtotal'),
    discount: t('cart.discount', 'Discount'),
    fees: t('cart.fees', 'Fees'),
    taxes: t('cart.taxes', 'Taxes'),
    tip: t('cart.tip', 'Tip'),
    total: t('cart.total', paymentComplete ? 'Total Paid' : 'Total Due'),
  };

  const handleKeyDownForInfoIcon = (event: KeyboardEvent<HTMLElement>): void => {
    if (event.key !== 'Enter') {
      return;
    }
    UI.openDutchiePayInstoreTaxInfo();
  };

  const infoIcon = (
    <IconContainer
      tabIndex={0}
      role='button'
      onKeyDown={handleKeyDownForInfoIcon}
      onClick={UI.openDutchiePayInstoreTaxInfo}
    >
      <InfoIcon />
    </IconContainer>
  );

  return (
    <Container>
      <TotalItem label={labels.subtotal} amount={subtotalCents} />
      {!!totalDiscounts && <TotalItem label={labels.discount} amount={totalDiscounts} discount />}
      {!!totalFees && <TotalItem label={labels.fees} amount={totalFees} />}
      <TotalItem label={labels.taxes} amount={totalTaxes} icon={infoIcon} />
      {(!InstoreCart.tippingDisabled || tip !== 0) && <TotalItem label={labels.tip} amount={tip} />}
      <TotalItem label={labels.total} amount={totalPrice} accented />
    </Container>
  );
};

const InfoIcon = styled.img.attrs({ alt: `info-icon`, src: `/icons/info-icon.svg` })`
  height: 13px;
  width: 13px;
  cursor: pointer;
`;

const IconContainer = styled.div`
  display: flex;
  height: 100%;
  width: 100%;
  margin-left: 4px;
  align-items: center;
`;

const Container = styled.div`
  display: flex;
  align-items: flex-start;
  flex-direction: column;
  width: 100%;
  gap: 8px;
`;
